// 7-day calendar view for to-dos. Each column is a day starting Mon
// of the displayed week; to-dos slot into the column matching their
// dueAt's calendar date in local time. Todos without a due date go
// into a "No due date" column on the far right.

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar } from "./Avatar";
import { DC } from "../sheets/createSlideOver/primitives";
import { isOverdue, priorityChipClass } from "../utils";
import type { Todo } from "../../../../../../shared/team-planner/schemas";

interface TodoCalendarProps {
  todos: Todo[];
  onOpen: (id: string) => void;
}

function startOfWeek(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day + 6) % 7; // shift so Monday = 0
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - diff);
  return date;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function fmtCol(d: Date): { weekday: string; day: string } {
  return {
    weekday: d.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase(),
    day: d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" }),
  };
}

export function TodoCalendar({ todos, onOpen }: TodoCalendarProps) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  const days = useMemo(() => {
    const list: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      list.push(d);
    }
    return list;
  }, [weekStart]);

  const grouped = useMemo(() => {
    const map = new Map<string, Todo[]>();
    for (const d of days) map.set(d.toDateString(), []);
    const noDue: Todo[] = [];
    for (const t of todos) {
      if (!t.dueAt) {
        noDue.push(t);
        continue;
      }
      const key = days.find((d) => sameDay(d, t.dueAt!));
      if (key) map.get(key.toDateString())?.push(t);
    }
    return { map, noDue };
  }, [days, todos]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className="bsp-btn bsp-btn-secondary bsp-btn-icon"
          onClick={() => {
            const d = new Date(weekStart);
            d.setDate(d.getDate() - 7);
            setWeekStart(d);
          }}
          aria-label="Previous week"
        >
          <ChevronLeft size={16} />
        </button>
        <div
          className="bsp-mono"
          style={{
            padding: "8px 12px",
            border: `1px solid ${DC.border}`,
            borderRadius: 4,
            background: DC.surface,
            font: "700 12px/1 var(--bsp-font-mono)",
            letterSpacing: "0.04em",
            color: DC.dark,
          }}
        >
          Week of {weekStart.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" })}
        </div>
        <button
          type="button"
          className="bsp-btn bsp-btn-secondary bsp-btn-icon"
          onClick={() => {
            const d = new Date(weekStart);
            d.setDate(d.getDate() + 7);
            setWeekStart(d);
          }}
          aria-label="Next week"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          className="bsp-btn bsp-btn-ghost bsp-btn-sm"
          onClick={() => setWeekStart(startOfWeek(new Date()))}
        >
          Today
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          gap: 8,
        }}
      >
        {days.map((d) => {
          const fmt = fmtCol(d);
          const list = grouped.map.get(d.toDateString()) ?? [];
          const today = sameDay(d, new Date());
          return (
            <div
              key={d.toISOString()}
              className="tp-kanban-column"
              style={{ minHeight: 220, padding: 10 }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  paddingBottom: 6,
                  borderBottom: `1px solid ${DC.border}`,
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    font: "700 10px/1 var(--bsp-font-sans)",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: today ? DC.primary : DC.grey,
                  }}
                >
                  {fmt.weekday}
                </span>
                <span
                  style={{
                    font: "700 14px/1 var(--bsp-font-mono)",
                    color: today ? DC.primary : DC.dark,
                  }}
                >
                  {fmt.day}
                </span>
              </div>
              {list.length === 0 ? (
                <div className="tp-empty" style={{ padding: "12px 0", fontSize: 11 }}>
                  No items
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {list.map((t) => (
                    <CalendarTile key={t.id} todo={t} onOpen={onOpen} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="tp-kanban-column" style={{ minHeight: 220, padding: 10 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              paddingBottom: 6,
              borderBottom: `1px solid ${DC.border}`,
              marginBottom: 8,
            }}
          >
            <span style={{ font: "700 10px/1 var(--bsp-font-sans)", letterSpacing: "0.14em", textTransform: "uppercase", color: DC.grey }}>
              No due
            </span>
            <span style={{ font: "700 14px/1 var(--bsp-font-mono)", color: DC.grey }}>
              ·
            </span>
          </div>
          {grouped.noDue.length === 0 ? (
            <div className="tp-empty" style={{ padding: "12px 0", fontSize: 11 }}>
              —
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {grouped.noDue.map((t) => (
                <CalendarTile key={t.id} todo={t} onOpen={onOpen} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CalendarTile({ todo, onOpen }: { todo: Todo; onOpen: (id: string) => void }) {
  const overdue = isOverdue(todo.dueAt, todo.status);
  return (
    <button
      type="button"
      onClick={() => onOpen(todo.id)}
      style={{
        textAlign: "left",
        padding: "6px 8px",
        background: DC.surface,
        border: `1px solid ${overdue ? "var(--bsp-red)" : DC.border}`,
        borderLeft: `3px solid ${overdue ? "var(--bsp-red)" : "var(--bsp-blue-2)"}`,
        borderRadius: 4,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div
        style={{
          font: "600 12px/1.3 var(--bsp-font-sans)",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {todo.title}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "space-between" }}>
        <span className={priorityChipClass(todo.priority)} style={{ fontSize: 9, padding: "1px 6px", height: 16 }}>
          {todo.priority}
        </span>
        {todo.primaryOwner ? <Avatar owner={todo.primaryOwner} size={18} /> : null}
      </div>
    </button>
  );
}
